import i18n from '@/i18n'
import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { Database } from '@/lib/supabase'

type Account = Database['public']['Tables']['accounts']['Row']
type Agency = Database['public']['Tables']['agencies']['Row']

interface AgencyStats {
  totalAccounts: number
  activeAccounts: number
  totalContacts: number
  totalMessages: number
  totalCampaigns: number
}

interface UseAgencyDataReturn {
  accounts: Account[]
  agency: Agency | null
  stats: AgencyStats | null
  loading: boolean
  error: string | null
  refetchAccounts: () => Promise<void>
  refetchAgency: () => Promise<void>
  updateAgency: (data: Partial<Agency>) => Promise<boolean>
  updateAccount: (
    accountId: string,
    data: Pick<Account, 'nome'>
  ) => Promise<{ success: boolean; error?: string }>
}

export const useAgencyData = (): UseAgencyDataReturn => {
  const { user } = useAuth()
  const [accounts, setAccounts] = useState<Account[]>([])
  const [agency, setAgency] = useState<Agency | null>(null)
  const [stats, setStats] = useState<AgencyStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAccounts = async (): Promise<void> => {
    if (!user?.account?.agency_id || !supabase) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('accounts')
        .select('*')
        .eq('agency_id', user.account.agency_id)
        .eq('is_active', true)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      setAccounts(data || [])
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar contas')
    } finally {
      setLoading(false)
    }
  }

  const fetchAgency = async (): Promise<void> => {
    if (!user?.account?.agency_id || !supabase) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('agencies')
        .select('*')
        .eq('id', user.account.agency_id)
        .maybeSingle()

      if (fetchError) throw fetchError

      setAgency(data)
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar agência')
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async (): Promise<void> => {
    if (!user?.account?.agency_id || !supabase) return

    try {
      const accountIds = accounts.map(acc => acc.id)

      if (accountIds.length === 0) {
        setStats({
          totalAccounts: 0,
          activeAccounts: 0,
          totalContacts: 0,
          totalMessages: 0,
          totalCampaigns: 0,
        })
        return
      }

      const [contactsRes, messagesRes, campaignsRes] = await Promise.all([
        supabase
          .from('account_contacts')
          .select('id', { count: 'exact', head: true })
          .in('account_id', accountIds),
        supabase
          .from('account_messages')
          .select('id', { count: 'exact', head: true })
          .in('account_id', accountIds),
        supabase
          .from('account_campaigns')
          .select('id', { count: 'exact', head: true })
          .in('account_id', accountIds),
      ])

      setStats({
        totalAccounts: accounts.length,
        activeAccounts: accounts.length,
        totalContacts: contactsRes.count || 0,
        totalMessages: messagesRes.count || 0,
        totalCampaigns: campaignsRes.count || 0,
      })
    } catch (err: any) {
      console.error('Erro ao buscar estatísticas:', err)
    }
  }

  const updateAgency = async (data: Partial<Agency>): Promise<boolean> => {
    if (!user?.account?.agency_id || !supabase) return false

    try {
      setError(null)
      const { error: updateError } = await supabase
        .from('agencies')
        .update(data)
        .eq('id', user.account.agency_id)

      if (updateError) throw updateError

      await fetchAgency()
      return true
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar agência')
      return false
    }
  }

  /**
   * Atualiza uma subconta da agência.
   *
   * O update usa `select` porque RLS não retorna erro quando bloqueia a operação:
   * a query simplesmente afeta zero linhas. Sem essa checagem a tela exibiria
   * sucesso sem ter salvado nada.
   *
   * A falha volta no retorno em vez de ir para `error`, que é reservado para o
   * carregamento inicial e troca a tela toda pelo estado de erro.
   */
  const updateAccount = async (
    accountId: string,
    data: Pick<Account, 'nome'>
  ): Promise<{ success: boolean; error?: string }> => {
    if (!supabase) {
      return { success: false, error: 'Supabase não está configurado' }
    }

    try {
      const { data: updated, error: updateError } = await supabase
        .from('accounts')
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq('id', accountId)
        .select('id')

      if (updateError) throw updateError

      if (!updated || updated.length === 0) {
        throw new Error(i18n.t('agency:errors.noPermissionEditSubaccount'))
      }

      await fetchAccounts()
      return { success: true }
    } catch (err) {
      console.error('❌ [useAgencyData] Erro ao atualizar subconta:', err)
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao atualizar subconta'
      }
    }
  }

  useEffect(() => {
    if (user?.account?.agency_id) {
      fetchAccounts()
      fetchAgency()
    }
  }, [user?.account?.agency_id])

  useEffect(() => {
    if (accounts.length > 0) {
      fetchStats()
    }
  }, [accounts])

  return {
    accounts,
    agency,
    stats,
    loading,
    error,
    refetchAccounts: fetchAccounts,
    refetchAgency: fetchAgency,
    updateAgency,
    updateAccount,
  }
}
